import styled, { x, css } from '@xstyled/styled-components';
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import ReactDOM from 'react-dom';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { Pause, Play } from '~/ui/buttons';
import CheckIcon from '~/assets/icons/check.svg';
import {
  CardAudioProgress,
  CardPlayButton,
  CARD_COLORS,
  formatAudioTime
} from '../../audio-player/HorizontalAudioPlayer';
import { Card } from '~/ui/card/Card';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { SpeakerIcon } from './SpeakerIcon';
import { HotspotPlusIcon } from './HotspotPlusIcon';
import { HotspotNumberLabel } from './HotspotNumberLabel';
import { TickMarkIcon } from './TickMarkIcon';
import { usePopup } from './usePopup';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useCanControl } from '~/ui/video-player/useCanControl';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { drillAnimation } from '~/ui/animation';
import { HotspotFocusScope } from '~/lib/a11y/HotspotFocusScope';
import { isMobile } from 'react-device-detect';
import { TrainingState, useTrainingStore } from '~/training/state';
import { t } from 'ttag';
import closeIcon from '~/assets/icons/close.svg';
import { EditorMediaFile, useEditorStore } from '~/shell/Store';
import NarrationPopup from '~/editor/narration/NarrationPopup';
import { ImageMapPopupLayoutContext } from '../../image-map/ImageMapPopupLayoutContext';
import { getImageMapBoundsEl } from '../../image-map/imageMapBounds';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';
import { stripHtml } from '~/lib/sanitization';
import {
  centerPopupInViewport,
  clampPopupLeftToViewport,
  shouldCenterPopupForReflow
} from '../../popup/popupViewportLayout';

const selector = (s: MediaPlayerStore) =>
  select(
    s,
    'loadMedia',
    'status',
    'toggle',
    'play',
    'loadSubtitles',
    'pause',
    'currentTime'
  );

const playerSelector = (s: MediaPlayerStore) => s.player();

const seqSelector = (s: SequenceStore) =>
  select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');

interface DrillBoxProps {
  $btnSize?: number;
  $drillBg?: string;
  $drillOpacity?: number;
}

const DrillBoxAnimation = styled.div<DrillBoxProps>`
  width: ${p => `${p.$btnSize ?? 84}px`};
  height: ${p => `${p.$btnSize ?? 84}px`};
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  /* inset + margin centers without transform (drillAnimation owns transform;
     odd sizes + uncentered absolute look shifted in Safari). */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  background-color: ${p => p.$drillBg ?? p.theme.colors.accentDark};
  border-radius: 50%;
  opacity: ${p => p.$drillOpacity ?? 0.5};
  z-index: 1;
`;

const CloseIcon = styled(closeIcon)`
  transform: rotate(180deg);
  color: ${p => p.theme.colors.black};
  width: 14px;
  height: 14px;
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 1;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.colors.white};
  border: 2px solid ${p => p.theme.colors.black};
  border-radius: 4px; /* square with subtle rounding */
  padding: 0;
  cursor: pointer;

  &:hover {
    background: ${p => p.theme.colors.greyLight};
  }
`;

const HotspotButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  background-color: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.black};
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
  z-index: 2;
  padding: 0;
  cursor: pointer;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const selectorVisualFeedback = (s: TrainingState) =>
  select(s, 'visualFeedback');

const PopupContent = styled.div<{
  isScrollEnabled?: boolean;
  gridArea?: string;
}>`
  ${p =>
    p.isScrollEnabled &&
    css`
      max-height: 200px;
      overflow-y: scroll;
    `}
`;

export const PopupButton = ({
  mediaUrl,
  id,
  name,
  body,
  blocking,
  subtitlesUrl,
  title,
  replay,
  isPopupCentered,
  openPopupUnderButton = false,
  popupFixedOffsetLeftPx: itemOffsetLeft,
  popupFixedOffsetTopPx: itemOffsetTop,
  shouldShow = true,
  btnSize = 64,
  scroll = false,
  parentId,
  editable = true,
  generated = false
}: {
  mediaUrl?: string;
  id: string;
  name: string;
  body: string;
  shouldShow: boolean;
  blocking: boolean;
  subtitlesUrl?: string;
  title?: number | string;
  replay?: boolean;
  isPopupCentered?: boolean;
  openPopupUnderButton?: boolean;
  popupFixedOffsetLeftPx?: number;
  popupFixedOffsetTopPx?: number;
  btnSize?: number;
  scroll?: boolean;
  parentId?: string;
  editable?: boolean;
  generated?: boolean;
}) => {
  const { contentEditable, updateFile } = useEditorStore();
  const [showAudioPopup, setShowAudioPopup] = useState(false);

  const { open, setOpen, popupRef } = usePopup();
  const { loadMedia, status, toggle, play, loadSubtitles, pause, currentTime } =
    useMediaPlayerStore(selector, shallow);
  const player = useMediaPlayerStore(playerSelector);
  const [duration, setDuration] = useState(0);
  const ref = useRef<HTMLDivElement>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [top, setTop] = useState<number>();
  const [left, setLeft] = useState<number>();
  const [isClicked, setIsClicked] = useState<boolean>(false);
  // Replaying a completed popup must not revoke its accepted completion.
  const audioCompleted = useRef(false);
  const { addBlocker, removeBlocker, setAudioPlayed } = useSequenceStore(
    seqSelector,
    shallow
  );

  const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
  const imageMapPopupLayout = useContext(ImageMapPopupLayoutContext);
  const offsetLeft =
    itemOffsetLeft ?? imageMapPopupLayout?.popupFixedOffsetLeftPx ?? 0;
  const offsetTop =
    itemOffsetTop ?? imageMapPopupLayout?.popupFixedOffsetTopPx ?? 0;
  /** CAT-1228 / WCAG 1.4.10: center at ≤320 CSS px (400% zoom), ignore offsets. */
  const centerInViewport =
    Boolean(isPopupCentered) || shouldCenterPopupForReflow();

  // CAT-1217 / WCAG 2.4.11: keep Tab inside the open popup so focus cannot
  // land on hotspots fully obscured behind it (e.g. Shift+Tab).
  useFocusTrap(popupRef, open, undefined, buttonRef);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      pause();
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, pause, setOpen]);

  useGlobalMedia();

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  useEffect(() => {
    if (globalPlayer && globalPlayer.pause) {
      try {
        globalPlayer.pause();
      } catch (e) {
        // Ignore errors when pausing uninitialized player
      }
    }
  }, []);

  useEffect(() => {
    if (blocking && !mediaUrl) {
      addBlocker(id);
    }

    return () => {
      removeBlocker(id);
    };
  }, [id, blocking]);

  useEffect(() => {
    if (!blocking) return;

    if (mediaUrl && status === 'done') audioCompleted.current = true;
    if (mediaUrl && !audioCompleted.current) addBlocker(id);

    if (
      (mediaUrl && (status === 'nomedia' || audioCompleted.current)) ||
      (!mediaUrl && open)
    ) {
      removeBlocker(id);
    }
  }, [status, open, mediaUrl, blocking, id]);

  useLayoutEffect(() => {
    if (!open) return;

    let attempts = 0;
    const maxAttempts = 15;

    function updatePosition() {
      const rect = ref?.current?.getBoundingClientRect();
      const popupEl = popupRef?.current;
      const popupRect = popupEl?.getBoundingClientRect();
      if (!rect || !popupEl) return;

      const popupReady =
        popupRect && popupRect.width >= 8 && popupRect.height >= 8;

      if (!popupReady && attempts++ < maxAttempts) {
        requestAnimationFrame(updatePosition);
        return;
      }

      if (!popupRect || popupRect.width < 4) return;

      const anchoredBelowHotspot = () => {
        let nextLeft = rect.left + rect.width / 2 - popupRect.width / 2;
        const shouldClamp =
          openPopupUnderButton || imageMapPopupLayout?.clampPopupToImageMap;
        if (shouldClamp) {
          const mapEl = getImageMapBoundsEl(ref.current ?? undefined);
          const mapRect = mapEl?.getBoundingClientRect();
          if (mapRect) {
            const pad = 8;
            nextLeft = Math.max(
              mapRect.left + pad,
              Math.min(mapRect.right - popupRect.width - pad, nextLeft)
            );
          }
        }
        nextLeft = clampPopupLeftToViewport(
          nextLeft,
          popupRect.width,
          offsetLeft
        );
        setTop(rect.bottom);
        setLeft(nextLeft);
      };

      // CAT-1228: ≤320 CSS px (DevTools or ~400% page zoom) → center in viewport.
      if (isPopupCentered || shouldCenterPopupForReflow()) {
        const { left: nextLeft, top: nextTop } = centerPopupInViewport(
          popupRect.width,
          popupRect.height
        );
        setTop(nextTop);
        setLeft(nextLeft);
        return;
      }

      if (openPopupUnderButton) {
        anchoredBelowHotspot();
        return;
      }

      anchoredBelowHotspot();
    }

    window.addEventListener('resize', updatePosition);
    updatePosition();

    return () => window.removeEventListener('resize', updatePosition);
  }, [
    open,
    openPopupUnderButton,
    isPopupCentered,
    imageMapPopupLayout,
    offsetLeft
  ]);

  useEffect(() => {
    if (!mediaUrl) return;
    loadMedia(mediaUrl, subtitlesUrl, 'audio', false, 0);
    loadSubtitles(subtitlesUrl);
  }, []);

  useEffect(() => {
    if (!player) {
      setDuration(0);
      return;
    }

    const updateDuration = () => {
      const nextDuration = player.duration();
      if (Number.isFinite(nextDuration) && nextDuration > 0) {
        setDuration(nextDuration);
      }
    };

    player.on('loadedmetadata', updateDuration);
    player.on('durationchange', updateDuration);
    updateDuration();

    return () => {
      player.off('loadedmetadata', updateDuration);
      player.off('durationchange', updateDuration);
    };
  }, [player, mediaUrl]);

  useEffect(() => {
    if (status != 'done' && !open) {
      pause();
    }
  }, [status, open]);

  useEffect(() => {
    if (status === 'done') {
      setOpen(false);
      setAudioPlayed(id);
    }
  }, [status]);

  // Hide the button when another media player is playing
  const canControl = useCanControl() || status != 'playing';

  const clickHandler = () => {
    if (contentEditable && editable) {
      setShowAudioPopup(true);
      return;
    }

    if (globalPlayer) {
      try {
        globalPlayer.pause();
      } catch (e) {
        // Ignore errors when pausing uninitialized player
      }
    }

    if (!mediaUrl) {
      setIsClicked(true);
      removeBlocker(id);
    }

    if (status === 'done' && !replay) return;

    play();
    setOpen(true);
  };

  const [isScrollEnabled, setScrollEnabled] = useState(false);

  if (scroll) {
    useEffect(() => {
      const handleResize = () => {
        if (popupRef?.current?.clientHeight! + 36 >= window.innerHeight) {
          setScrollEnabled(true);
        }
      };

      isMobile ? setScrollEnabled(true) : setTimeout(() => handleResize(), 0);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [open]);
  }

  /** CAT-1373 / EL–E140: visible title must name the dialog via aria-labelledby. */
  const dialogTitleId = `audio-popup-dialog-title-${id}`;

  const getContentForPopup = (name: string, body: string) => {
    const isDefaultAudioPointTitle =
      !mediaUrl && name?.trim() === 'Audio Point';

    const displayName = isDefaultAudioPointTitle ? '' : name;
    const titleProps = displayName ? { id: dialogTitleId } : {};

    if (displayName && body == '' && shouldShow) {
      return (
        <x.h2
          {...titleProps}
          overflow="hidden"
          textOverflow="ellipsis"
          gridArea="title"
          mb="15px"
          ml="-15px"
          dangerouslySetInnerHTML={{ __html: generated ? '' : displayName }}
        />
      );
    } else {
      return (
        <PopupContent
          isScrollEnabled={isScrollEnabled || false}
          tabIndex={isScrollEnabled ? 0 : -1}
          gridArea="title"
          style={
            !mediaUrl && (!displayName || displayName.trim() === '')
              ? { paddingTop: '16px' }
              : undefined
          }
        >
          {shouldShow && (
            <>
              {displayName && (
                <x.h2
                  {...titleProps}
                  mb="15px"
                  dangerouslySetInnerHTML={{ __html: displayName }}
                />
              )}
              <div
                dangerouslySetInnerHTML={{ __html: generated ? '' : body }}
              />
            </>
          )}
        </PopupContent>
      );
    }
  };

  const getStartIcon = () => {
    // Symbol mode (no marker / legacy '+'): speaker. Number mode: numeric label.
    if (title == null || title === '' || title === '+') {
      return <SpeakerIcon className="pressable" aria-hidden />;
    }
    return (
      <HotspotNumberLabel className="pressable">{title}</HotspotNumberLabel>
    );
  };

  const isListened =
    (mediaUrl && status === 'done' && !replay) || (!mediaUrl && isClicked);
  // CAT-1245 / CAT-1370 / EL–E137: prefer author topic; otherwise distinguish
  // hotspots by marker number or body snippet (authors often leave name empty).
  const audioTopic = stripHtml(name).trim();
  const hasAudioTopic =
    Boolean(audioTopic) &&
    audioTopic !== 'Audio Point' &&
    audioTopic !== 'Point';
  const hotspotMarker =
    title != null && String(title).trim() !== '' && String(title) !== '+'
      ? String(title).trim()
      : '';
  const bodySnippet = stripHtml(body || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 80);
  const disambiguator = hasAudioTopic
    ? audioTopic
    : hotspotMarker
      ? t`Hotspot ${hotspotMarker}`
      : bodySnippet;
  const ariaLabelBase = isListened
    ? t`Audio already played`
    : t`Play the audio`;
  const ariaLabel = disambiguator
    ? `${ariaLabelBase}: ${disambiguator}`
    : ariaLabelBase;
  /** Same visibility rule as the h2 in getContentForPopup (CAT-1373). */
  const hasVisibleDialogTitle = Boolean(
    stripHtml(
      !mediaUrl && name?.trim() === 'Audio Point' ? '' : name || ''
    ).trim()
  );
  const dialogFallbackLabel = disambiguator || t`Play the audio`;

  const getBtnIcon = () => {
    // Text-only: after open, always show seen (✓) — including Number / '+' modes.
    // Must run before title checks; otherwise numbered markers never leave the digit.
    if (isClicked && !mediaUrl) {
      return <TickMarkIcon className="pressable" />;
    }

    if (title === '+') {
      return <HotspotPlusIcon className="pressable" aria-hidden />;
    }

    if (title) {
      return (
        <HotspotNumberLabel className="pressable">{title}</HotspotNumberLabel>
      );
    }

    // Text-only Symbol mode: Figma Icon/plus (not a font glyph)
    return <HotspotPlusIcon className="pressable" aria-hidden />;
  };

  const onSelectNarration = (audio: EditorMediaFile) => {
    if (id && audio?.id) {
      updateFile(id.toString(), audio, 'audios', parentId);
    }

    setShowAudioPopup(false);
  };

  return (
    <HotspotFocusScope
      w={`${btnSize}px`}
      h={`${btnSize}px`}
      backgroundColor={
        canControl && status === 'done'
          ? 'rgba(255,255,255,0.2)'
          : 'transparent'
      }
      borderRadius="50%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      position="relative"
      zIndex={4}
      pointerEvents="auto"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      className="pressable audioPopUpBtn"
    >
      {canControl && (status !== 'done' || replay) && visualFeedback && (
        <DrillBoxAnimation
          className="drillBoxEffect"
          $btnSize={btnSize - 4}
          $drillOpacity={1}
        />
      )}
      {open &&
        status == 'playing' &&
        ReactDOM.createPortal(
          <>
            <MediaCaptions />
          </>,
          document.getElementById('root')!
        )}
      {canControl && (
        <HotspotButton
          ref={buttonRef}
          onClick={clickHandler}
          style={{
            width: `${btnSize - 10}px`,
            height: `${btnSize - 10}px`
          }}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="pressable"
        >
          {mediaUrl ? (
            status === 'done' ? (
              <TickMarkIcon className="pressable" aria-hidden />
            ) : (
              getStartIcon()
            )
          ) : (
            getBtnIcon()
          )}
        </HotspotButton>
      )}
      <AudioPlayer />
      {open &&
        ReactDOM.createPortal(
          <Card
            ref={popupRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={hasVisibleDialogTitle ? dialogTitleId : undefined}
            aria-label={hasVisibleDialogTitle ? undefined : dialogFallbackLabel}
            position="fixed"
            // CAT-1228 / WCAG 1.4.10: never wider than viewport − 32px pad at 320 CSS px
            style={{
              width: 'min(350px, calc(100vw - 32px))',
              maxWidth: '350px'
            }}
            p="md"
            onClick={e => e.stopPropagation()}
            top={`${(top ?? 0) + (centerInViewport ? 0 : offsetTop)}px`}
            left={`${(left ?? 0) + (centerInViewport ? 0 : offsetLeft)}px`}
            display="grid"
            gridTemplateColumns="auto 1fr"
            gridTemplateRows="auto auto"
            gridTemplateAreas="'button title' 'button progress'"
            paddingTop={mediaUrl ? '16px' : '40px'}
            gap={mediaUrl ? 'md' : 'md'}
            tabIndex={-1}
          >
            {!mediaUrl && (
              <CloseButton
                onClick={() => setOpen(false)}
                aria-label={t`close`}
                role="button"
              >
                <CloseIcon />
              </CloseButton>
            )}
            {getContentForPopup(name, body)}
            {mediaUrl && (
              <x.div
                style={{ gridColumn: '1 / -1' }}
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="8px"
                w="100%"
              >
                <CardPlayButton
                  type="button"
                  onClick={toggle}
                  aria-label={
                    status === 'playing'
                      ? 'Pause'
                      : status === 'done'
                        ? 'Replay'
                        : 'Play'
                  }
                >
                  {status === 'playing' ? (
                    <Pause />
                  ) : status === 'done' ? (
                    <CheckIcon />
                  ) : (
                    <Play id="playBtnSvg" />
                  )}
                </CardPlayButton>
                <x.div
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                  gap="4px"
                  flex="1"
                  minWidth="0"
                >
                  <x.div
                    fontWeight={600}
                    fontSize="14px"
                    lineHeight="20px"
                    style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      color: CARD_COLORS.text
                    }}
                  >
                    {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
                  </x.div>
                  <CardAudioProgress w="100%" />
                </x.div>
              </x.div>
            )}
          </Card>,
          document.getElementById('root')!
        )}
      {showAudioPopup &&
        contentEditable &&
        ReactDOM.createPortal(
          <NarrationPopup
            onClose={() => setShowAudioPopup(false)}
            skipUpdate={true}
            onSelectCallback={onSelectNarration}
            id={id}
            skipSequenceNarration={true}
          />,
          document.body
        )}
    </HotspotFocusScope>
  );
};
